import { Box, Center, Flex, Img, Stack, Tag, Text, Wrap, WrapItem } from '@chakra-ui/react'
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons'

import { pluralize } from '@/utils/helpers'
import { toHumanReadableString } from '@/utils/time'

import { useServerNow } from '@/client/hooks'

import PublicImage from '@/components/PublicImage/PublicImage'
import Icon from '@/components/UI/Icon/Icon'
import Tippy from '@/components/UI/Tippy'
import OptionalLink from '@/components/Worlds/UI/OptionalLink'

import type { WorldDetailsForTodoList } from './TodoList'
import type { TodoItem } from './types'

const DEFAULT_ICON_PATH = '/worlds/icons/Schoolhouse-Community.svg'

export default function TodoSingle({
  world,
  title,
  link,
  deadline,
  tag,
  emoji,
  stepsRemaining,
  disabled,
  onClick,
  additionalComponent,
  includeWorldInfo = false,
  openLinkInNewTab = false,
}: TodoItem & {
  world: WorldDetailsForTodoList | null
  includeWorldInfo?: boolean
}): JSX.Element {
  const now = useServerNow()
  return (
    <Tippy disabled={disabled?.tooltip == null} content={disabled?.tooltip}>
      <OptionalLink href={disabled ? undefined : link} width="100%" isExternal={openLinkInNewTab}>
        <Flex
          direction="row"
          align="center"
          justify="space-between"
          borderWidth="2px"
          borderStyle="solid"
          borderRadius="8px"
          borderColor="border.muted"
          p="16px"
          bg={disabled ? 'bg.muted' : 'bg'}
          _hover={{ borderColor: disabled ? 'none' : 'border.emphasized' }}
          onClick={disabled == null ? onClick : undefined}
          cursor={disabled == null ? 'pointer' : 'not-allowed'}
        >
          <Stack
            direction="row"
            spacing="8px"
            key={`${title}${deadline != null ? `-${deadline.toISO()}` : ''}`}
            align="center"
          >
            {!includeWorldInfo ? (
              <Center width="36px" height="36px" borderRadius="8px" fontSize="32px">
                {disabled == null ? emoji : '🔒'}
              </Center>
            ) : (
              <Box minWidth="32px" height="32px" position="relative">
                {world == null ? (
                  <PublicImage
                    src={DEFAULT_ICON_PATH}
                    width="21px"
                    height="21px"
                    bg="primary.solid"
                    borderRadius="4px"
                  />
                ) : (
                  <Img
                    src={world.iconUrl}
                    width="21px"
                    height="21px"
                    bg={world.primaryColor}
                    borderRadius="4px"
                  />
                )}
                <Center
                  position="absolute"
                  width="21px"
                  height="21px"
                  right={0}
                  bottom={0}
                  border="2px solid"
                  borderColor="bg"
                  bg="bg"
                  borderRadius="50%"
                  flexShrink={0}
                >
                  <Icon faIcon={faCircleCheck} color="primary.fg" size="md" />
                </Center>
              </Box>
            )}

            <Box>
              {includeWorldInfo && world != null ? (
                <Text variant="eyebrow-small">{world.title}</Text>
              ) : null}
              <Wrap align="center" spacingX="8px" spacingY="4px">
                <WrapItem>
                  <Text fontWeight="bold" noOfLines={1}>
                    {title}
                  </Text>
                </WrapItem>
                {stepsRemaining == null ? null : (
                  <WrapItem>
                    <Tag
                      bg={disabled ? 'neutrals.emphasized' : 'primary.solid'}
                      color="primary.contrast"
                      fontSize="12px"
                    >
                      {pluralize(stepsRemaining, 'step')} left
                    </Tag>
                  </WrapItem>
                )}
                {deadline == null ? null : (
                  <WrapItem>
                    <Tag
                      bg={disabled ? 'neutrals.emphasized' : 'warning.solid'}
                      color="warning.contrast"
                      fontSize="12px"
                    >
                      Due {toHumanReadableString(deadline.diff(now))}
                    </Tag>
                  </WrapItem>
                )}

                {tag == null ? null : (
                  <WrapItem>
                    <Tag bg="secondary.solid" color="secondary.contrast" fontSize="12px">
                      {tag.title}
                    </Tag>
                  </WrapItem>
                )}
              </Wrap>
            </Box>
          </Stack>
          {additionalComponent?.()}
        </Flex>
      </OptionalLink>
    </Tippy>
  )
}
